import { Link } from '@tanstack/react-router';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import {
  type SpecOut,
  useCreateSpec,
  useDeleteSpec,
  useSpecs,
} from '@/lib/hooks/useAudits';

/**
 * /app/specs — manage monitored OpenAPI specs.
 * Cap meter at top. Table + add form. Respects tier cap (server enforces).
 */
export function SpecsPage() {
  const { data, isLoading } = useSpecs();
  const remaining =
    !data || data.limit === -1 ? Number.POSITIVE_INFINITY : data.limit - data.used;
  const isAtLimit = Number.isFinite(remaining) && remaining <= 0;

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Specs</h1>
          <p className="mt-1 text-sm text-slate-500">
            Your OpenAPI specs — Athena audits each one across 10 dimensions.
          </p>
        </div>
      </header>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Usage</CardTitle>
          <CardDescription>
            {data ? (
              <>
                Using <strong>{data.used}</strong> of{' '}
                <strong>{data.limit === -1 ? 'unlimited' : data.limit}</strong> specs on your plan.
              </>
            ) : (
              'Loading…'
            )}
          </CardDescription>
        </CardHeader>
        {isAtLimit && (
          <p className="text-sm text-[var(--color-warning)]">
            You've hit your plan's limit.{' '}
            <Link to="/app/billing" className="underline">Upgrade</Link> to add more.
          </p>
        )}
      </Card>

      <AddSpecForm disabled={isAtLimit || isLoading} />

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">URL</th>
              <th className="px-4 py-3 font-medium">GitHub</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {isLoading && <SkeletonRow />}
            {data && data.items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                  No specs yet. Add one above to run your first audit.
                </td>
              </tr>
            )}
            {data?.items.map((s) => <SpecRow key={s.id} spec={s} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-4 py-3"><div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-800" /></td>
      <td className="px-4 py-3"><div className="h-4 w-48 rounded bg-slate-200 dark:bg-slate-800" /></td>
      <td className="px-4 py-3"><div className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-800" /></td>
      <td className="px-4 py-3"><div className="h-4 w-16 rounded bg-slate-200 dark:bg-slate-800" /></td>
      <td className="px-4 py-3" />
    </tr>
  );
}

function SpecRow({ spec }: { spec: SpecOut }) {
  const del = useDeleteSpec();
  const onDelete = () => {
    if (confirm(`Remove "${spec.name}"? This won't delete past audits.`)) {
      del.mutate(spec.id);
    }
  };
  return (
    <tr>
      <td className="px-4 py-3 font-medium">{spec.name}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">
        <a href={spec.url} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">
          {spec.url}
        </a>
      </td>
      <td className="px-4 py-3 text-xs">{spec.github_repo ?? '—'}</td>
      <td className="px-4 py-3">
        {spec.active ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-400">
            Inactive
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <Button variant="ghost" size="icon" onClick={onDelete} aria-label={`Delete ${spec.name}`}>
          <Trash2Icon className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

function AddSpecForm({ disabled }: { disabled: boolean }) {
  const create = useCreateSpec();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ name: name.trim(), url: url.trim() });
      setName('');
      setUrl('');
    } catch (err) {
      if (err instanceof ApiError) {
        const d = err.detail as { message?: string; error?: string } | undefined;
        setError(d?.message ?? d?.error ?? `Error ${err.status}`);
      } else {
        setError((err as Error).message);
      }
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Add a spec</CardTitle>
        <CardDescription>Paste the public URL to your OpenAPI (JSON or YAML).</CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-[1fr_2fr_auto] md:items-end">
        <div className="space-y-1.5">
          <Label htmlFor="spec-name">Name</Label>
          <Input
            id="spec-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Main API"
            disabled={disabled}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="spec-url">OpenAPI URL</Label>
          <Input
            id="spec-url"
            type="url"
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://api.example.com/openapi.json"
            disabled={disabled}
          />
        </div>
        <Button type="submit" disabled={disabled || create.isPending}>
          <PlusIcon className="h-4 w-4" />
          {create.isPending ? 'Adding…' : 'Add spec'}
        </Button>
      </form>
      {error && (
        <p className="mt-3 text-sm text-[var(--color-critical)]" aria-live="polite">
          {error}
        </p>
      )}
    </Card>
  );
}
